import React, { useState } from "react";
import { Permission, Traveler } from "types";
import { IsGranted } from "@/components/Common/IsGranted";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { CustomerRecordSummary } from "@/components/Customers/Contacts/CustomerRecordSummary";
import { UnmergeCustomerRecordModal } from "@/components/Customers/Contacts/UnmergeCustomerRecordModal";
import { useCollection } from "@/hooks/useCollection";
import { getMergedTravelerIds, isMergedAway } from "@/models/User";
import { getSeller } from "@/utils/getSeller";

// Hydrates the merged-away references (see getMergedTravelerIds); unresolved ids are legacy and skipped.
export const MergedCustomerRecords = ({ contact }: { contact: Traveler }) => {
    const seller = getSeller();
    const [unmergeEntry, setUnmergeEntry] = useState<Traveler>();

    // Merged-away records can't restore their own absorbed records, so hide the panel instead of
    // showing dead-end Unmerge buttons.
    const mergedIds = isMergedAway(contact) ? [] : getMergedTravelerIds(contact);
    const { data: mergedTravelers } = useCollection<Traveler>(
        "users",
        { "id[in]": mergedIds.join(","), "mergedInto[exists]": true, type: 2, seller: seller.id },
        { enabled: mergedIds.length > 0 },
    );

    if (mergedTravelers.length === 0) {
        return null;
    }

    return (
        <div className="pt-10">
            <h3 className="mb-5">Merged Customer Records</h3>
            <div className="grid grid-cols-2 gap-6">
                {mergedTravelers.map((merged) => (
                    <MergedRecordCard key={merged.id} merged={merged} onUnmerge={() => setUnmergeEntry(merged)} />
                ))}
            </div>

            <UnmergeCustomerRecordModal
                contact={contact}
                mergeEntry={unmergeEntry}
                isOpen={!!unmergeEntry}
                onClose={() => setUnmergeEntry(undefined)}
            />
        </div>
    );
};

const MergedRecordCard = ({ merged, onUnmerge }: { merged: Traveler; onUnmerge: () => void }) => {
    return (
        <div className="flex items-start justify-between rounded-md p-4">
            <CustomerRecordSummary
                boldName
                name={merged.name}
                email={merged.email}
                phone={merged.phone}
                mergedAt={merged.mergedAt}
            />

            <IsGranted permissions={Permission.MODIFY_CUSTOMER_DETAILS}>
                <SecondaryButton size="small" onClick={onUnmerge}>
                    Unmerge
                </SecondaryButton>
            </IsGranted>
        </div>
    );
};
